import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Trophy, Flame, User, Users, Timer, Pause, Play, ChevronDown, BookOpen } from "@/lib/icons";
import GameLayout from "@/games/shared/GameLayout";
import GameMenu from "@/games/shared/GameMenu";
import PlayerSetup from "@/games/shared/PlayerSetup";
import Scoreboard from "@/games/shared/Scoreboard";
import TurnTimer from "@/games/shared/TurnTimer";
import MatchResultDialog from "@/games/shared/MatchResultDialog";
import { useHotSeatMatch, type PlayerId } from "@/games/shared/useHotSeatMatch";
import { play } from "@/games/shared/sound";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  RACE_TURN_SECONDS,
  FAST_THRESHOLD_S,
  POINTS_FAST,
  POINTS_SLOW,
  generateRaceQuestion,
  scoreRaceAnswer,
  type RaceQuestion,
} from "@/games/reactionRace/engine";

const QUESTIONS_PER_MATCH = 10;
const RESULT_PAUSE_MS = 1900;
const SOLO_BEST_KEY = "elementpad:reaction-race:best";

type RaceMode = "solo" | "hot-seat";

const readBest = (): number => {
  try {
    return Number(window.localStorage.getItem(SOLO_BEST_KEY)) || 0;
  } catch {
    return 0;
  }
};
const writeBest = (score: number) => {
  try {
    window.localStorage.setItem(SOLO_BEST_KEY, String(score));
  } catch {
    /* ignore */
  }
};

/**
 * Reaction Race - predict products or reaction types under a tight clock.
 * Solo mode tracks personal best; hot-seat alternates 10 questions per player.
 */
export default function ReactionRace() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<RaceMode | null>(null);

  if (!mode) return <ModeSelect onPick={setMode} />;
  if (mode === "solo") {
    return <SoloMatch onChangeMode={() => setMode(null)} onExit={() => navigate("/play")} />;
  }
  return <HotSeatMatch onChangeMode={() => setMode(null)} onExit={() => navigate("/play")} />;
}

function ModeSelect({ onPick }: { onPick: (m: RaceMode) => void }) {
  return (
    <GameLayout
      title="Reaction Race"
      subtitle="Predict the product or reaction type before the clock runs out."
      headerActions={<GameMenu gameKey="reaction-race" />}
    >
      <div className="max-w-md mx-auto rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-1">
          <Flame size={16} className="text-primary" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-foreground">Choose a mode</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          {QUESTIONS_PER_MATCH} questions · {RACE_TURN_SECONDS}s each · ≤{FAST_THRESHOLD_S}s = {POINTS_FAST} pts.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => onPick("solo")}
            className="text-left p-4 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex items-center gap-2 mb-1">
              <User size={14} className="text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold text-foreground">Solo</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Run the clock. Personal best saved on this device.
            </p>
          </button>
          <button
            type="button"
            onClick={() => onPick("hot-seat")}
            className="text-left p-4 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex items-center gap-2 mb-1">
              <Users size={14} className="text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold text-foreground">Hot-seat 2P</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Alternate questions on one device. Higher total wins.
            </p>
          </button>
        </div>
      </div>
    </GameLayout>
  );
}

// ----- solo -----

type SoloPhase =
  | { kind: "playing"; index: number; question: RaceQuestion; startedAt: number; score: number }
  | { kind: "result"; index: number; question: RaceQuestion; chosenIndex: number | null; score: number; lastAward: number; outcome: "fast" | "slow" | "wrong" | "timeout" }
  | { kind: "finished"; score: number; best: number };

function SoloMatch({ onChangeMode, onExit }: { onChangeMode: () => void; onExit: () => void }) {
  const askedRef = useRef<Set<string>>(new Set());
  const advance = useRef<number | null>(null);
  const [paused, setPaused] = useState(false);
  const pausedAtRef = useRef<number | null>(null);
  const [phase, setPhase] = useState<SoloPhase>(() => {
    const q = generateRaceQuestion(Math.random, new Set());
    askedRef.current.add(q.id);
    return { kind: "playing", index: 0, question: q, startedAt: performance.now(), score: 0 };
  });

  useEffect(() => () => {
    if (advance.current) window.clearTimeout(advance.current);
  }, []);

  const handlePause = useCallback(() => {
    if (phase.kind !== "playing" || paused) return;
    pausedAtRef.current = performance.now();
    setPaused(true);
  }, [phase.kind, paused]);

  const handleResume = useCallback(() => {
    if (!paused) return;
    if (pausedAtRef.current != null && phase.kind === "playing") {
      const delta = performance.now() - pausedAtRef.current;
      setPhase({ ...phase, startedAt: phase.startedAt + delta });
    }
    pausedAtRef.current = null;
    setPaused(false);
  }, [paused, phase]);

  const answer = useCallback(
    (chosenIndex: number | null) => {
      if (phase.kind !== "playing" || paused) return;
      const elapsed = performance.now() - phase.startedAt;
      const { points, outcome } = scoreRaceAnswer(chosenIndex, phase.question, elapsed);
      const next = phase.score + points;
      if (outcome === "fast" || outcome === "slow") play("correct");
      else if (outcome === "wrong") play("wrong");

      setPhase({
        kind: "result",
        index: phase.index,
        question: phase.question,
        chosenIndex,
        score: next,
        lastAward: points,
        outcome,
      });

      advance.current = window.setTimeout(() => {
        const i = phase.index + 1;
        if (i >= QUESTIONS_PER_MATCH) {
          const best = readBest();
          if (next > best) writeBest(next);
          play(next > best ? "win" : "click");
          setPhase({ kind: "finished", score: next, best: Math.max(best, next) });
          return;
        }
        const q = generateRaceQuestion(Math.random, askedRef.current);
        askedRef.current.add(q.id);
        setPhase({ kind: "playing", index: i, question: q, startedAt: performance.now(), score: next });
      }, RESULT_PAUSE_MS);
    },
    [phase, paused],
  );

  const onExpire = useCallback(() => answer(null), [answer]);

  const restart = () => {
    if (advance.current) window.clearTimeout(advance.current);
    askedRef.current = new Set();
    setPaused(false);
    pausedAtRef.current = null;
    const q = generateRaceQuestion(Math.random, new Set());
    askedRef.current.add(q.id);
    setPhase({ kind: "playing", index: 0, question: q, startedAt: performance.now(), score: 0 });
  };

  if (phase.kind === "finished") {
    return (
      <GameLayout
        title="Reaction Race"
        subtitle="Solo run"
        headerActions={<GameMenu gameKey="reaction-race" onReset={restart} resetLabel="Restart" />}
      >
        <div className="max-w-md mx-auto rounded-xl border border-border bg-card p-6 text-center">
          <Trophy size={28} className="mx-auto text-primary mb-2" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">Final score</p>
          <p className="text-4xl font-bold font-mono text-foreground tabular-nums mb-1">
            {phase.score}
          </p>
          <p className="text-xs text-muted-foreground mb-5">
            Personal best: <span className="font-mono">{phase.best}</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button variant="outline" onClick={onExit}>Quit</Button>
            <Button variant="outline" onClick={onChangeMode}>Change mode</Button>
            <Button onClick={restart}>Play again</Button>
          </div>
        </div>
      </GameLayout>
    );
  }

  const active = phase.kind === "playing" ? phase : null;
  const result = phase.kind === "result" ? phase : null;
  const q = active?.question ?? result!.question;
  const idx = active?.index ?? result!.index;
  const score = active?.score ?? result!.score;

  return (
    <GameLayout
      title="Reaction Race"
      subtitle="Solo run"
      headerActions={<GameMenu gameKey="reaction-race" onReset={restart} resetLabel="Restart" />}
    >
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="Question" value={`${idx + 1} / ${QUESTIONS_PER_MATCH}`} />
        <Stat label="Score" value={String(score)} mono />
        <div className="rounded-lg border border-border bg-card p-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Time</span>
          <div className="flex items-center gap-1.5">
            {active ? (
              <>
                <TurnTimer
                  key={`r-${q.id}`}
                  seconds={RACE_TURN_SECONDS}
                  onExpire={onExpire}
                  size={40}
                  paused={paused}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handlePause}
                  disabled={paused}
                  aria-label="Pause match"
                  className="h-7 w-7"
                >
                  <Pause size={14} aria-hidden="true" />
                </Button>
              </>
            ) : (
              <Timer size={18} className="text-muted-foreground" aria-hidden="true" />
            )}
          </div>
        </div>
      </div>

      <RaceQuestionCard
        question={q}
        chosenIndex={result?.chosenIndex ?? null}
        onChoose={(i) => active && !paused && answer(i)}
        revealed={!!result}
      />

      {result && (
        <RaceResultBanner
          outcome={result.outcome}
          award={result.lastAward}
          explanation={q.explanation}
          whyCorrect={q.whyCorrect}
        />
      )}

      {paused && active && <PauseOverlay onResume={handleResume} />}
    </GameLayout>
  );
}

// ----- hot-seat -----

type HotSeatPhase =
  | { kind: "answering"; question: RaceQuestion; startedAt: number }
  | { kind: "result"; question: RaceQuestion; chosenIndex: number | null; lastAward: number; outcome: "fast" | "slow" | "wrong" | "timeout" }
  | { kind: "handoff"; nextPlayer: PlayerId };

function HotSeatMatch({ onChangeMode, onExit }: { onChangeMode: () => void; onExit: () => void }) {
  const match = useHotSeatMatch(0);
  const [phase, setPhase] = useState<HotSeatPhase | null>(null);
  const askedRef = useRef<Set<string>>(new Set());
  const advance = useRef<number | null>(null);
  const [paused, setPaused] = useState(false);
  const pausedAtRef = useRef<number | null>(null);
  const TOTAL_TURNS = QUESTIONS_PER_MATCH * 2;

  useEffect(() => () => {
    if (advance.current) window.clearTimeout(advance.current);
  }, []);

  const startMatch = useCallback(
    (p1: string, p2: string) => {
      askedRef.current = new Set();
      match.start(p1, p2);
      setPhase({ kind: "handoff", nextPlayer: 0 });
    },
    [match],
  );

  const beginTurn = useCallback(() => {
    const q = generateRaceQuestion(Math.random, askedRef.current);
    askedRef.current.add(q.id);
    setPhase({ kind: "answering", question: q, startedAt: performance.now() });
  }, []);

  const handlePause = useCallback(() => {
    if (!phase || phase.kind !== "answering" || paused) return;
    pausedAtRef.current = performance.now();
    setPaused(true);
  }, [phase, paused]);

  const handleResume = useCallback(() => {
    if (!paused) return;
    if (pausedAtRef.current != null && phase?.kind === "answering") {
      const delta = performance.now() - pausedAtRef.current;
      setPhase({ ...phase, startedAt: phase.startedAt + delta });
    }
    pausedAtRef.current = null;
    setPaused(false);
  }, [paused, phase]);

  const submit = useCallback(
    (chosenIndex: number | null) => {
      if (!phase || phase.kind !== "answering" || paused) return;
      const elapsed = performance.now() - phase.startedAt;
      const { points, outcome } = scoreRaceAnswer(chosenIndex, phase.question, elapsed);
      const current = match.state.currentTurn;
      const newScore = match.state.stats[current] + points;
      match.setStat(current, newScore);

      if (outcome === "fast" || outcome === "slow") play("correct");
      else if (outcome === "wrong") play("wrong");

      setPhase({
        kind: "result",
        question: phase.question,
        chosenIndex,
        lastAward: points,
        outcome,
      });

      advance.current = window.setTimeout(() => {
        if (match.state.turnNumber >= TOTAL_TURNS) {
          const a = match.state.stats[0] + (current === 0 ? points : 0);
          const b = match.state.stats[1] + (current === 1 ? points : 0);
          const winner: PlayerId | null = a === b ? null : a > b ? 0 : 1;
          play(winner === null ? "click" : "win");
          match.finish(winner);
          return;
        }
        match.nextTurn();
        const next: PlayerId = current === 0 ? 1 : 0;
        setPhase({ kind: "handoff", nextPlayer: next });
      }, RESULT_PAUSE_MS);
    },
    [phase, match, TOTAL_TURNS, paused],
  );

  const onExpire = useCallback(() => submit(null), [submit]);

  const handleRematch = () => {
    if (advance.current) window.clearTimeout(advance.current);
    askedRef.current = new Set();
    setPaused(false);
    pausedAtRef.current = null;
    match.start(match.state.players[0].name, match.state.players[1].name);
    setPhase({ kind: "handoff", nextPlayer: 0 });
  };
  const handleChangePlayers = () => {
    if (advance.current) window.clearTimeout(advance.current);
    askedRef.current = new Set();
    setPaused(false);
    pausedAtRef.current = null;
    match.reset();
    setPhase(null);
  };

  if (match.state.status === "setup") {
    return (
      <GameLayout
        title="Reaction Race"
        subtitle={`Hot-seat · ${QUESTIONS_PER_MATCH} questions each`}
        headerActions={<GameMenu gameKey="reaction-race" />}
      >
        <PlayerSetup
          title="Set up the match"
          description="Two players, alternating questions. Pass the device after each turn."
          ctaLabel="Start race"
          onStart={startMatch}
        />
        <div className="max-w-md mx-auto mt-3 text-center">
          <button
            type="button"
            onClick={onChangeMode}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Change mode
          </button>
        </div>
      </GameLayout>
    );
  }

  const matchOver = match.state.status === "finished";
  const current = match.state.players[match.state.currentTurn];
  const turnsRemaining = Math.max(0, TOTAL_TURNS - match.state.turnNumber + 1);

  return (
    <GameLayout
      title="Reaction Race"
      subtitle="Hot-seat 2-player"
      headerActions={<GameMenu gameKey="reaction-race" onReset={handleRematch} resetLabel="Restart" />}
    >
      <div className="mb-5">
        <Scoreboard state={match.state} statLabel="Score" />
      </div>

      {phase?.kind === "handoff" && !matchOver && (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground mb-1">Pass the device to:</p>
          <p className="text-2xl font-bold text-foreground mb-1">{current.name}</p>
          <p className="text-[11px] text-muted-foreground mb-4">
            Turn {match.state.turnNumber} of {TOTAL_TURNS} · {turnsRemaining} left
          </p>
          <Button onClick={beginTurn} size="lg">
            <Flame size={14} className="mr-1.5" aria-hidden="true" />
            I'm ready
          </Button>
        </div>
      )}

      {phase?.kind === "answering" && !matchOver && (
        <>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] text-muted-foreground">{current.name}'s turn</span>
            <div className="flex items-center gap-1.5">
              <TurnTimer
                key={`hr-${phase.question.id}`}
                seconds={RACE_TURN_SECONDS}
                onExpire={onExpire}
                size={44}
                paused={paused}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handlePause}
                disabled={paused}
                aria-label="Pause match"
                className="h-7 w-7"
              >
                <Pause size={14} aria-hidden="true" />
              </Button>
            </div>
          </div>
          <RaceQuestionCard
            question={phase.question}
            chosenIndex={null}
            onChoose={(i) => !paused && submit(i)}
            revealed={false}
          />
          {paused && <PauseOverlay onResume={handleResume} />}
        </>
      )}

      {phase?.kind === "result" && !matchOver && (
        <>
          <RaceQuestionCard
            question={phase.question}
            chosenIndex={phase.chosenIndex}
            onChoose={() => undefined}
            revealed
          />
          <RaceResultBanner
            outcome={phase.outcome}
            award={phase.lastAward}
            explanation={phase.question.explanation}
            whyCorrect={phase.question.whyCorrect}
          />
        </>
      )}

      <MatchResultDialog
        state={match.state}
        open={matchOver}
        onRematch={handleRematch}
        onChangePlayers={handleChangePlayers}
        onExit={onExit}
        detail={`${QUESTIONS_PER_MATCH} questions each. Higher total wins.`}
      />
    </GameLayout>
  );
}

// ----- shared sub-components -----

function PauseOverlay({ onResume }: { onResume: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Match paused"
    >
      <div className="max-w-sm w-[90%] rounded-xl border border-border bg-card p-6 text-center shadow-lg">
        <Pause size={28} className="mx-auto text-primary mb-2" aria-hidden="true" />
        <h2 className="text-lg font-semibold text-foreground mb-1">Match paused</h2>
        <p className="text-sm text-muted-foreground mb-5">
          The timer is frozen. Resume when you're ready to continue.
        </p>
        <Button onClick={onResume} size="lg" className="w-full" autoFocus>
          <Play size={14} className="mr-1.5" aria-hidden="true" />
          Resume
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
        {label}
      </div>
      <div
        className={`text-base font-semibold text-foreground ${
          mono ? "font-mono tabular-nums" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function RaceQuestionCard({
  question,
  chosenIndex,
  onChoose,
  revealed,
}: {
  question: RaceQuestion;
  chosenIndex: number | null;
  onChoose: (i: number) => void;
  revealed: boolean;
}) {
  return (
    <div className="rounded-xl border border-primary/40 bg-card p-5 mb-3">
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="outline" className="text-[10px]">
          {question.kind === "predict-product" ? "Predict the product" : "Identify the type"}
        </Badge>
      </div>
      <h2 className="text-base font-semibold text-foreground mb-2 leading-snug">
        {question.prompt}
      </h2>
      <p className="text-sm font-mono text-foreground bg-muted/50 px-3 py-2 rounded mb-4 break-words">
        {question.formula}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="group" aria-label="Answer choices">
        {question.choices.map((choice, i) => {
          const isCorrect = i === question.correctIndex;
          const isChosen = i === chosenIndex;
          const stateClasses = revealed
            ? isCorrect
              ? "border-primary bg-primary/10 text-foreground"
              : isChosen
                ? "border-destructive/60 bg-destructive/10 text-foreground"
                : "border-border bg-background text-muted-foreground"
            : "border-border bg-background hover:border-primary/60 hover:bg-accent/40";
          return (
            <button
              key={i}
              type="button"
              onClick={() => !revealed && onChoose(i)}
              disabled={revealed}
              className={`text-left p-3 rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${stateClasses} disabled:cursor-default`}
            >
              <span className="inline-flex items-center justify-center w-5 h-5 mr-2 rounded text-[10px] font-bold bg-muted text-muted-foreground">
                {String.fromCharCode(65 + i)}
              </span>
              <span className="text-sm font-mono">{choice}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RaceResultBanner({
  outcome,
  award,
  explanation,
  whyCorrect,
}: {
  outcome: "fast" | "slow" | "wrong" | "timeout";
  award: number;
  explanation: string;
  whyCorrect: string;
}) {
  const positive = outcome === "fast" || outcome === "slow";
  return (
    <div
      role="status"
      aria-live="polite"
      className={`rounded-md border p-3 ${
        positive ? "border-primary/40 bg-primary/5" : "border-destructive/30 bg-destructive/5"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        {positive ? (
          <CheckCircle2 size={14} className="text-primary" aria-hidden="true" />
        ) : (
          <XCircle size={14} className="text-destructive" aria-hidden="true" />
        )}
        <span className="text-sm font-semibold text-foreground">
          {outcome === "fast" && `Fast & correct! +${award} pts`}
          {outcome === "slow" && `Correct. +${award} pts`}
          {outcome === "wrong" && `Wrong - no points`}
          {outcome === "timeout" && `Time's up - no points`}
        </span>
        {positive && (
          <Badge variant="secondary" className="text-[10px] ml-auto">
            {outcome === "fast" ? "Speed bonus" : "Standard"}
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{explanation}</p>
      <WhyCorrect text={whyCorrect} defaultOpen={!positive} />
    </div>
  );
}

function WhyCorrect({ text, defaultOpen = false }: { text: string; defaultOpen?: boolean }) {
  return (
    <details
      className="mt-2 group rounded-md border border-border/60 bg-background/40 open:bg-background/60"
      open={defaultOpen}
    >
      <summary className="cursor-pointer list-none px-2.5 py-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md">
        <BookOpen size={12} className="text-primary" aria-hidden="true" />
        <span>Why this is correct</span>
        <ChevronDown
          size={12}
          className="ml-auto text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <p className="px-2.5 pb-2.5 pt-0.5 text-xs text-muted-foreground leading-relaxed">
        {text}
      </p>
    </details>
  );
}
